import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Folder, ArrowLeft, Plus, Trash2, FolderTree, Edit } from "lucide-react";

export default function CategoriesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, category: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [formData, setFormData] = useState({
    name: "",
    parent_id: "",
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Kategorien");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API}/categories`, {
        name: formData.name,
        parent_id: formData.parent_id || null,
      });
      toast.success("Kategorie erfolgreich erstellt");
      setCreateDialog(false);
      setFormData({ name: "", parent_id: "" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Kategorie");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      await axios.put(`${API}/categories/${editDialog.category.id}`, {
        name: formData.name,
        parent_id: formData.parent_id || null,
      });
      toast.success("Kategorie erfolgreich aktualisiert");
      setEditDialog({ open: false, category: null });
      setFormData({ name: "", parent_id: "" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren der Kategorie");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await axios.delete(`${API}/categories/${deleteDialog.category.id}`);
      toast.success("Kategorie erfolgreich gelöscht");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen der Kategorie");
    } finally {
      setDeleteDialog({ open: false, category: null });
    }
  };

  const getCategoryTree = () => {
    const categoryMap = {};
    categories.forEach((cat) => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });

    const tree = [];
    categories.forEach((cat) => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else if (!cat.parent_id) {
        tree.push(categoryMap[cat.id]);
      }
    });

    return tree;
  };

  const renderCategoryTree = (nodes, level = 0) => {
    return nodes.map((node) => (
      <div key={node.id} style={{ marginLeft: `${level * 24}px` }}>
        <div
          className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg mb-2 hover:bg-slate-50 transition-colors"
          data-testid={`category-item-${node.id}`}
        >
          <div className="flex items-center gap-3">
            {level === 0 ? (
              <Folder className="w-5 h-5 text-blue-600" />
            ) : (
              <FolderTree className="w-5 h-5 text-slate-600" />
            )}
            <span className="font-medium text-slate-900">{node.name}</span>
            {node.children.length > 0 && (
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                {node.children.length} Unterkategorien
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setFormData({ name: node.name, parent_id: node.parent_id || "" });
                setEditDialog({ open: true, category: node });
              }}
              data-testid={`edit-category-${node.id}`}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeleteDialog({ open: true, category: node })}
              data-testid={`delete-category-${node.id}`}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {node.children.length > 0 && renderCategoryTree(node.children, level + 1)}
      </div>
    ));
  };

  const parentCategories = categories.filter((cat) => !cat.parent_id);
  const categoryTree = getCategoryTree();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="icon"
                data-testid="back-button"
                className="border-slate-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                  <Folder className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Kategorien</h1>
                  <p className="text-sm text-slate-600">{categories.length} Kategorien</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => setCreateDialog(true)}
              data-testid="create-category-button"
              className="bg-purple-600 hover:bg-purple-700 text-white h-10 px-4 rounded-lg font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Kategorie erstellen
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-slate-50 rounded-2xl">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-white rounded-2xl border border-slate-200">
              <Folder className="w-16 h-16 mb-4 text-slate-300" />
              <p className="text-lg font-medium">Keine Kategorien gefunden</p>
              <p className="text-sm">Erstellen Sie Ihre erste Kategorie</p>
            </div>
          ) : (
            <div className="space-y-2">{renderCategoryTree(categoryTree)}</div>
          )}
        </div>
      </main>

      {/* Create Category Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Kategorie erstellen</DialogTitle>
            <DialogDescription>Erstellen Sie eine neue Kategorie oder Unterkategorie</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700 font-medium">
                Kategoriename
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="category-name-input"
                className="mt-2 h-11 border-slate-300"
                placeholder="z.B. Elektronik"
              />
            </div>
            <div>
              <Label htmlFor="parent" className="text-slate-700 font-medium">
                Übergeordnete Kategorie (optional)
              </Label>
              <Select
                value={formData.parent_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="mt-2 h-11 border-slate-300" data-testid="parent-category-select">
                  <SelectValue placeholder="Keine (Hauptkategorie)" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="none">Keine (Hauptkategorie)</SelectItem>
                  {parentCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialog(false)}
                data-testid="create-category-cancel-button"
                className="border-slate-300"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={creating}
                data-testid="create-category-submit-button"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {creating ? "Wird erstellt..." : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, category: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kategorie bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie den Namen oder die übergeordnete Kategorie.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCategory} className="space-y-4">
            <div>
              <Label htmlFor="edit-category-name">Kategoriename *</Label>
              <Input
                id="edit-category-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-2"
                placeholder="z.B. Elektronik"
              />
            </div>

            <div>
              <Label htmlFor="edit-parent-category">Übergeordnete Kategorie</Label>
              <Select
                value={formData.parent_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger id="edit-parent-category" className="mt-2">
                  <SelectValue placeholder="Keine (Hauptkategorie)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine (Hauptkategorie)</SelectItem>
                  {categories
                    .filter((cat) => cat.id !== editDialog.category?.id)
                    .map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditDialog({ open: false, category: null });
                  setFormData({ name: "", parent_id: "" });
                }}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={updating || !formData.name}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {updating ? "Wird aktualisiert..." : "Aktualisieren"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Category Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, category: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Kategorie "{deleteDialog.category?.name}" wirklich löschen? Kategorien mit
              Unterkategorien können nicht gelöscht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-category-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-category-confirm-button"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
